/**
 * AskBaskyFullPage — /training/assistant route. Full-width chat backed by
 * Claude Sonnet 4.5 via POST /api/ai/ask. Session persists across reloads
 * via sessionStorage.
 */
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Sparkles, Send, ArrowRight, BookOpen, ThumbsUp, ThumbsDown, Copy, Loader2, Plus, Trash2, Edit2, Save, X, Brain } from 'lucide-react';
import { toast } from 'sonner';
import { getStarters } from '../../components/askBasky/askBaskyKnowledge';
import api from '../../lib/api';

const SESSION_KEY = 'basky_session_v2';
const THREAD_KEY = 'basky_thread_full_v2';

function loadThread() {
  try { return JSON.parse(sessionStorage.getItem(THREAD_KEY) || '[]'); }
  catch { return []; }
}

export default function AskBaskyFullPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [thread, setThread] = useState(loadThread());
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || null);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [feedback, setFeedback] = useState({});
  const scrollRef = useRef(null);

  const starters = getStarters(location.pathname);

  useEffect(() => { sessionStorage.setItem(THREAD_KEY, JSON.stringify(thread)); }, [thread]);
  useEffect(() => { if (sessionId) sessionStorage.setItem(SESSION_KEY, sessionId); }, [sessionId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, typing]);

  const ask = async (q) => {
    setThread(t => [...t, { role: 'user', id: `u-${Date.now()}`, text: q, at: new Date().toISOString() }]);
    setInput('');
    setTyping(true);
    try {
      const r = await api.post('/ai/ask', { question: q, session_id: sessionId });
      if (r.data?.session_id && r.data.session_id !== sessionId) setSessionId(r.data.session_id);
      setThread(t => [...t, {
        role: 'assistant',
        id: `a-${Date.now()}`,
        text: r.data?.answer || "I couldn't produce an answer — please try again.",
        snapshot: r.data?.snapshot,
        at: new Date().toISOString(),
      }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Network error';
      setThread(t => [...t, {
        role: 'assistant',
        id: `a-${Date.now()}`,
        text: `**Something went wrong.** ${detail}`,
        error: true,
        at: new Date().toISOString(),
      }]);
    } finally {
      setTyping(false);
    }
  };

  const clearThread = async () => {
    if (sessionId) {
      try { await api.delete(`/ai/session/${sessionId}`); } catch { /* ignore */ }
    }
    setThread([]);
    setSessionId(null);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(THREAD_KEY);
    toast.success('Conversation cleared');
  };

  return (
    <div className="flex gap-4 p-6 h-[calc(100vh-4rem)]" data-testid="ask-basky-page">
      {/* Main chat column */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                Ask Basky
                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-violet-100 text-violet-700 rounded font-semibold">Claude 4.5</span>
              </h1>
              <p className="text-xs text-slate-500">Your AI assistant for the Training Mgmt hub — grounded on live DB</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearThread} disabled={thread.length === 0} data-testid="ask-basky-clear">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <Plus className="w-3.5 h-3.5 mr-1 rotate-45" /> Close
            </Button>
          </div>
        </div>

        <Card className="flex-1 overflow-hidden flex flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {thread.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6" />
                </div>
                <p className="text-lg font-semibold text-slate-800">How can I help?</p>
                <p className="text-xs text-slate-500 mt-1">Pick a suggestion from the sidebar or type your own question below.</p>
              </div>
            )}
            {thread.map(m =>
              m.role === 'user'
                ? <UserBubble key={m.id} text={m.text} />
                : <AssistantBubble key={m.id} msg={m}
                    feedback={feedback[m.id]}
                    onFeedback={(id, k) => {
                      setFeedback(f => ({ ...f, [id]: k }));
                      toast.success(k === 'up' ? 'Thanks!' : 'Noted');
                    }}
                    onCopy={(t) => { navigator.clipboard.writeText(t.replace(/\*\*/g, '')); toast.success('Copied'); }} />
            )}
            {typing && <TypingIndicator />}
          </div>
          <div className="border-t p-4 bg-slate-50/40">
            <form onSubmit={(e) => { e.preventDefault(); if (input.trim() && !typing) ask(input.trim()); }}>
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask about any module — e.g., 'How do I heal a mismatched invoice?'"
                  disabled={typing}
                  className="h-11 text-sm"
                  data-testid="ask-basky-page-input"
                />
                <Button type="submit" disabled={!input.trim() || typing} className="h-11 bg-violet-600 hover:bg-violet-700 px-5" data-testid="ask-basky-page-send">
                  <Send className="w-4 h-4 mr-1.5" /> Send
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>

      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 space-y-3">
        <Card className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Quick Prompts</p>
          <div className="space-y-1.5">
            {starters.map((s, i) => (
              <button key={i} onClick={() => ask(s)}
                className="w-full text-left px-3 py-2 bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 rounded-md text-xs text-slate-700 transition-colors group flex items-center justify-between"
                data-testid={`ask-basky-page-starter-${i}`}>
                <span>{s}</span>
                <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0 ml-2" />
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-3 bg-violet-50/40 border-violet-100">
          <p className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold mb-1.5 flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> How Basky works
          </p>
          <p className="text-[11px] text-slate-600 leading-relaxed">
            Powered by <strong>Claude Sonnet 4.5</strong> via Emergent Integrations.
            Every request is grounded with a fresh snapshot of your DB (students, CRM links,
            tickets, bookmarks) so answers cite real live numbers.
          </p>
        </Card>

        <KnowledgePanel />
      </div>
    </div>
  );
}

function KnowledgePanel() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', tags: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/ai/knowledge');
      setNotes(r.data?.notes || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ title: '', content: '', tags: '' }); setAdding(false); setEditingId(null); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required'); return;
    }
    const payload = {
      title: form.title.trim(),
      content: form.content.trim(),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.put(`/ai/knowledge/${editingId}`, payload);
        toast.success('Note updated — Basky will use it on next message');
      } else {
        await api.post('/ai/knowledge', payload);
        toast.success('Note added — Basky has learned it');
      }
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setAdding(true);
    setForm({ title: n.title, content: n.content, tags: (n.tags || []).join(', ') });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this knowledge note?')) return;
    try {
      await api.delete(`/ai/knowledge/${id}`);
      toast.success('Note deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="p-3" data-testid="basky-knowledge-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
          <Brain className="w-3 h-3" /> Teach Basky ({notes.length})
        </p>
        {!adding && (
          <button onClick={() => setAdding(true)}
            className="text-[10px] text-violet-700 hover:text-violet-900 font-semibold flex items-center gap-0.5"
            data-testid="basky-knowledge-add">
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>

      {adding && (
        <div className="space-y-1.5 mb-2 p-2 bg-violet-50/40 border border-violet-100 rounded">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Title (e.g., Refund policy)"
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-violet-400"
            data-testid="basky-knowledge-title" />
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="Content / process / rule. Markdown allowed."
            rows={5}
            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-violet-400 resize-none"
            data-testid="basky-knowledge-content" />
          <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
            placeholder="Tags, comma-separated (optional)"
            className="w-full px-2 py-1 text-[10px] border border-slate-200 rounded focus:outline-none focus:border-violet-400" />
          <div className="flex gap-1">
            <button onClick={save}
              className="flex-1 px-2 py-1 text-[10px] font-semibold bg-violet-600 text-white rounded hover:bg-violet-700 flex items-center justify-center gap-1"
              data-testid="basky-knowledge-save">
              <Save className="w-3 h-3" /> {editingId ? 'Update' : 'Save'}
            </button>
            <button onClick={reset}
              className="px-2 py-1 text-[10px] font-semibold bg-slate-200 text-slate-700 rounded hover:bg-slate-300 flex items-center justify-center gap-1">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[10px] text-slate-400 italic">Loading…</p>
      ) : notes.length === 0 && !adding ? (
        <p className="text-[10px] text-slate-500 leading-relaxed">
          No notes yet. Click <strong>+ Add</strong> to teach Basky a process, policy, or any domain knowledge it can't read from the codebase or DB.
        </p>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {notes.map(n => (
            <div key={n.id} className="group p-2 bg-white border border-slate-200 rounded text-[11px] hover:border-violet-300 transition-colors">
              <div className="flex items-start justify-between gap-1">
                <p className="font-semibold text-slate-800 flex-1 truncate" title={n.title}>{n.title}</p>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(n)} className="p-0.5 text-slate-400 hover:text-violet-600" title="Edit">
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button onClick={() => remove(n.id)} className="p-0.5 text-slate-400 hover:text-rose-600" title="Delete">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <p className="text-slate-500 line-clamp-2 mt-0.5">{n.content}</p>
              {n.tags?.length > 0 && (
                <div className="flex flex-wrap gap-0.5 mt-1">
                  {n.tags.map(t => (
                    <span key={t} className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-500 rounded">{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-slate-800">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const out = [];
  let listBuf = null;
  const flushList = () => {
    if (listBuf) { out.push(<ul key={`l-${out.length}`} className="list-disc pl-5 my-1 space-y-0.5">{listBuf}</ul>); listBuf = null; }
  };
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (/^\d+\.\s/.test(t)) { flushList(); out.push(<p key={i} className="my-1"><span className="font-semibold text-violet-700">{t.match(/^\d+/)[0]}. </span>{renderInline(t.replace(/^\d+\.\s/, ''))}</p>); }
    else if (t.startsWith('- ')) { listBuf = listBuf || []; listBuf.push(<li key={i}>{renderInline(t.slice(2))}</li>); }
    else if (t === '') { flushList(); out.push(<div key={i} className="h-1" />); }
    else { flushList(); out.push(<p key={i} className="my-1">{renderInline(t)}</p>); }
  });
  flushList();
  return out;
}

function UserBubble({ text }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[70%] bg-violet-600 text-white rounded-lg rounded-tr-none px-4 py-2.5 text-sm leading-relaxed">{text}</div>
    </div>
  );
}

function AssistantBubble({ msg, feedback, onFeedback, onCopy }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 max-w-[80%]">
        <div className={`border rounded-lg rounded-tl-none px-4 py-3 text-sm leading-relaxed shadow-sm ${msg.error ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-white border-slate-200 text-slate-700'}`}>
          {renderMarkdown(msg.text)}
        </div>
        <div className="flex items-center gap-0.5 mt-1 pl-1">
          <button onClick={() => onFeedback(msg.id, 'up')} className={`p-1 rounded hover:bg-slate-100 ${feedback === 'up' ? 'text-emerald-600' : 'text-slate-400'}`}><ThumbsUp className="w-3 h-3" /></button>
          <button onClick={() => onFeedback(msg.id, 'down')} className={`p-1 rounded hover:bg-slate-100 ${feedback === 'down' ? 'text-rose-600' : 'text-slate-400'}`}><ThumbsDown className="w-3 h-3" /></button>
          <button onClick={() => onCopy(msg.text)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><Copy className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg rounded-tl-none px-4 py-3 shadow-sm">
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
      </div>
    </div>
  );
}
